import InfoBox from "../ui/InfoBox"
import SectionHeader from "../ui/SectionHeader"
import { FiCheckSquare } from "react-icons/fi"
import { useResetListener } from "../../hooks/useResetListener"

export type PatentDeclarationsValue = {
  divisionalApplication: boolean
  entitledPersonFiling: boolean
  referenceFiling: boolean
  waiverOfInvitationToProceed: boolean
  feeReductionNaturalPersonOrEntity: boolean
  feeReductionMicroEntity: boolean
}

export const EMPTY_PATENT_DECLARATIONS: PatentDeclarationsValue = {
  divisionalApplication: false,
  entitledPersonFiling: false,
  referenceFiling: false,
  waiverOfInvitationToProceed: false,
  feeReductionNaturalPersonOrEntity: false,
  feeReductionMicroEntity: false
}

interface PatentDeclarationsSectionProps {
  value: PatentDeclarationsValue
  onChange: (value: PatentDeclarationsValue) => void
  disabled?: boolean
}

const DECLARATIONS: {
  key: keyof PatentDeclarationsValue
  label: string
}[] = [
  {
    key: "divisionalApplication",
    label: "Divisional application (Article 76 EPC)"
  },
  {
    key: "entitledPersonFiling",
    label:
      "Filing of a new European patent application by the entitled person (Article 61(1)(b) EPC, Rule 17 EPC)"
  },
  {
    key: "referenceFiling",
    label:
      "Filing by reference to a previously filed application (Rule 40(1)(c) EPC)"
  },
  {
    key: "waiverOfInvitationToProceed",
    label:
      "Waiver of the invitation to state whether the application is to be proceeded with (Rule 70(2) EPC)"
  },
  {
    key: "feeReductionNaturalPersonOrEntity",
    label:
      "The/Each applicant requests a fee reduction under Rule 7a(1) EPC and is a natural person or an entity under Rule 7a(2) EPC."
  },
  {
    key: "feeReductionMicroEntity",
    label:
      "The/Each applicant requests a fee reduction under Rule 7a(3) EPC and is a micro entity under Rule 7a(3) EPC."
  }
]

// The remaining declarations from the real EP1001 form's "Allgemeine
// Angaben" section, beyond language/title (SCRUM-55). Each is an
// independent, purely informational statement — none of them changes
// any other section's validation yet.
export default function PatentDeclarationsSection({
  value,
  onChange,
  disabled = false
}: PatentDeclarationsSectionProps) {
  useResetListener(() => {
    onChange(EMPTY_PATENT_DECLARATIONS)
  })

  const toggle = (key: keyof PatentDeclarationsValue) => {
    if (disabled) return
    onChange({ ...value, [key]: !value[key] })
  }

  return (
    <div>
      <SectionHeader icon={<FiCheckSquare size={20} />} title="Declarations">
        <InfoBox title="Optional declarations">
          These declarations only apply in specific situations. Leave
          them unchecked if none of them applies to this filing.
        </InfoBox>
      </SectionHeader>

      <div className="flex flex-col gap-3">
        {DECLARATIONS.map(({ key, label }) => (
          <label
            key={key}
            className="flex cursor-pointer items-start gap-2 text-sm text-gray-700"
          >
            <input
              type="checkbox"
              checked={value[key]}
              disabled={disabled}
              onChange={() => toggle(key)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-dantzBlue focus:ring-dantzBlue"
            />
            {label}
          </label>
        ))}
      </div>
    </div>
  )
}
